import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';

//@ts-ignore
import { Button as AntdButton } from 'antd';
import ReactBSAlert from 'react-bootstrap-sweetalert';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from 'reactstrap';

import DetailLayout from 'layouts/DetailLayout';
import { setCurrentDoc, setCurrentExam } from 'store/actions/docs/docsActions';
import LoadingSpinner from 'views/components/loadingSpinner/loadingSpinner';
import ExamDocApi from '../../../api/ExamDocApi';
import ExamResourceApi from '../../../api/ExamResourceApi';
import NavigationExamApi from '../../../api/NavigationExamApi';
import QuestionApi from '../../../api/QuestionApi';
import ApiCaller from '../../../lib/ApiCaller';
import Exam, { ExamContentElement } from '../../../lib/common/models/exam';
import ExamResource from '../../../lib/common/models/examResource';
import ExamResourceCollection from '../../../lib/common/models/examResourceCollection';
import MYDocument from '../../../lib/common/models/myDocument';
import Settings from '../../../lib/settings';
import {
  downloadAnswers,
  downloadDocument,
  sendToWritePDF,
} from '../../../shared/utils/ApiCommands';
import { getBase64Content } from '../../../shared/utils/Base64Content';
import { AppState } from '../../../store/store';
import './adhoc-styles.css';
import TinyMCETextEditor from '../../../components/TinyMCETextEditor';
import { getRegex } from './helper';
import * as Sentry from "@sentry/react";
import { NZDocumentType } from 'lib/common/models/nzDocument';
import { ISOStringToDMY } from 'utils/utils';
import QuestionSelector from './QuestionSelector';
import ExamDocumentStatuses from './examDocumentStatuses';
import Question from 'lib/common/models/question';

type MyProps = RouteComponentProps<{
  slug: string;
}>;

const ExamEditor: FC<MyProps> = () => {
  const [customName, setCustomName] = useState<string | undefined>();
  const [customDescription, setCustomDescription] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTip, setLoadingTip] = useState('');
  const [shuffleConfirm, setShuffleConfirm] = useState(false);
  const [resourceModal, setResourceModal] = useState(false);
  const [resourcesToAdd, setResourcesToAdd] = useState<ExamResource[]>([]);
  const [resources, setResources] = useState<ExamResource[]>([]);
  const [generatingPDF, setGeneratingPDF] = useState<boolean>(false);
  const [examQuestionContents, setExamQuestionContents] = useState<ExamContentElement[]>([]); // List of questions to select for an exam that selects manually
  const [isQuestionSelectorOpen, setIsQuestionSelectorOpen] = useState<boolean>(false); // Boolean that controls questions selector modal toggle
  const [selectedExamContent, setSelectedExamContent] = useState<ExamContentElement>(); // Selected exam content to change
  const [questionsWithUsage,setQuestionsWithUsage] = useState<Map<string,Question>>(new Map()); // If can shuffle questions, then this variable is used
  const [refreshConfirm, setRefreshConfirm] = useState(false); // Boolean that controls refresh confirmation modal
  const [refreshComment, setRefreshComment] = useState(''); // Comment for refresh operation
  const [selectedQuestionForRefresh, setSelectedQuestionForRefresh] = useState<ExamContentElement>(); // Selected question for refresh

  const currentDoc = useSelector((state: AppState) => state.current.doc);
  const currentExam = useSelector((state: AppState) => state.current.exam);
  const currentUser = useSelector((state: AppState) => state.session.userInfo);
  const editorContRef: any = useRef();
  const history = useHistory();
  const dispatch = useDispatch();

  // API CALL to load questions with usage, depending on canShuffleQuestions
  useEffect(() => {
    if(!currentDoc.id) {
      history.goBack()
    }
    // Set custom name and description for save draft... (Rescued from old commits and a use effect deleted due to conflicts)
    setCustomName(currentDoc.customName as string);
    setCustomDescription(currentDoc.customDescription as string);

    // Trigger this function when exam has questions, and each question has an id and can not shuffle (because if for extract extra info)
    const questionIds = currentExam.examContent.content.map(c => c.questionId);
    if(questionIds.length > 0 && questionIds.every( q => q.length > 0) && currentDoc.isSeto) {
      setLoading(true);
      setLoadingTip('Loading extra data for questions...');
      const questionApi = new QuestionApi(new ApiCaller(currentUser.token));
      questionApi.loadListWithUsage(questionIds).then((data) => {
        setLoading(false)
        setQuestionsWithUsage(new Map(data.questions.map(item => [item.id as string, item])));
        toast.success('Questions loaded successfully')
      }).catch((err) => {
        setLoading(false);
        console.error(err)
        toast.error('Error loading questions with usage')
      })
    }
    
  }, [currentExam])

  useEffect(() => {
    const examResourceApi = new ExamResourceApi(
      new ApiCaller(currentUser.token),
    );
    examResourceApi.getAll().then((data: ExamResourceCollection) => {
      if (currentExam?.metadata?.relatedDocuments) {
        const currentExamResources = currentExam.metadata.relatedDocuments.map(
          (item: any) => item._id,
        );
        setResources(
          data.resources.filter(
            (resource: ExamResource) =>
              !currentExamResources.includes(resource.id),
          ),
        );
      } else {
        setResources(data.resources);
      }
    });
  }, [currentExam]);

  const b64 = (newContent: string | null = null) => {
    if (newContent) {
      return getBase64Content(newContent);
    }
    return getBase64Content(currentDoc.content);
  };


  useEffect(() => {
    const navigationExamApi = new NavigationExamApi(
      new ApiCaller(currentUser.token),
    );
    setCustomName(currentDoc.customName);
    setCustomDescription(currentDoc.customDescription);
    navigationExamApi
      .getDraftById(currentDoc.id as string)
      .then((item) => {
        dispatch(setCurrentDoc(new MYDocument(item)));
      })
      .catch((err) => {
        Sentry.captureException(err);
      });

    if (currentDoc.sourceSlug) {
    navigationExamApi
      .getExamModelBySlug(currentDoc.sourceSlug)
      .then((exam) => {
        dispatch(setCurrentExam(exam));
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
    }
  }, []);

  const [examDoc, setExamDoc] = useState<any>('');

  useEffect(() => {
    if (currentDoc.content !== undefined) {
      setExamDoc(currentDoc);
    }
  }, [currentDoc]);

  const getEditor = (currentEditor: any) => {
    editorContRef.current = currentEditor;
  };

  /**
   * Shuffle exams
   */
  const shuffleDraft = () => {
    setLoadingTip('Shuffling exams...');
    setLoading(true);
    const navigationExamApi = new NavigationExamApi(new ApiCaller());
    navigationExamApi
      .shuffleExam(currentDoc.id as string, currentUser.token)
      .then((res) => {
        dispatch(setCurrentDoc(new MYDocument(res.doc)));
        dispatch(setCurrentExam(new Exam(res.exam)));
        editorContRef.current.setContent(new MYDocument(res.doc).content);
        setShuffleConfirm(false);
        setLoading(false);
        toast.success('Exam shuffled');
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.message);
      });
  };

  const saveDraft = (
    newDraftId?: string | null,
    newContent?: string | null,
  ) => {
    if (currentUser.type == 'guest') {
      return;
    }
    const examDocApi = new ExamDocApi(new ApiCaller());
    const userId = currentUser._id ? currentUser._id : 'FAKEUSER';
    let draftId = currentDoc.id ? currentDoc.id : 'ERROR';
    if (newDraftId) {
      draftId = newDraftId;
    }
    const vars: any[] = currentDoc.vars ?? [];
    const b64content = b64(newContent);
    if (currentDoc.sourceSlug) {
    examDocApi
      .updateDraft(
        draftId,
        b64content,
        currentDoc.sourceSlug as string,
        userId,
        vars,
        customName,
        customDescription,
        currentDoc.expirationDate,
      )
      .then(() => {
        toast.success('Draft updated successfully');
        dispatch(currentDoc);
      })
      .catch((err) => {
        console.error(err);
      });
    }
  };

  const updateDoc = (e: any) => {
    currentDoc.content = e;
  };

  const questionListIsEmpty = () => {
    return currentExam?.examContent.content[0]._id != 'nothing';
  };

  /**
   * Shuffle action for a question
   * @param question 
   */
  function onShuffleQuestion(question:ExamContentElement) {
    const examApi = new NavigationExamApi(new ApiCaller());
    setLoadingTip('Shuffling question...');
    setLoading(true);
    const questionId = question.questionId;
    examApi
      .shuffleExamQuestion(
        currentDoc.id,
        currentExam.id as string,
        questionId,
        question.topicId,
        currentUser.token,
      )
      .then(async (res) => {
        setLoading(false);
        let myDocument = new MYDocument(res.doc);
        const examContent = res.exam.examContent.content;
        const currentQuestionIDs = currentExam.examContent.content.map(
          (ce: any) => ce.questionId,
        );
        const newQuestionIDs = examContent.map(
          (content: any) => content.questionId,
        );
        const newQuestionId = newQuestionIDs.find(
          (id: string) => !currentQuestionIDs.includes(id),
        );
        if (newQuestionId) {
          const newQuestion = examContent.find(
            (ec: any) => ec.questionId === newQuestionId,
          );
          const regex = getRegex(questionId);

          const existQuestion = (currentDoc.content as string).match(regex);
          if (existQuestion && newQuestion) {
            myDocument.content = currentDoc.content?.replace(
              regex,
              `<div id="${newQuestionId}">${newQuestion.html}</div>`,
            );
          }
        } else {
          myDocument.content = currentDoc.content;
        }
        let myExam: any = new Exam(res.exam);

        if (currentExam.metadata.relatedDocuments.length) {
          const resources = currentExam.metadata.relatedDocuments.map(
            (relDoc: any) => ({ ...relDoc, id: relDoc._id }),
          );
          await examApi.addExamResources(currentExam.id as string, resources, currentUser.token);
        }
        myExam = {
          ...myExam,
          metadata: currentExam.metadata,
        };
        dispatch(setCurrentExam(myExam));
        dispatch(setCurrentDoc(myDocument));

        editorContRef.current.setContent(myDocument.content);
        toast.success('Question updated.');
        saveDraft(myDocument?.id, myDocument.content);
      })
      .catch((err) => {
        setLoading(false);
        toast.error('Unable to shuffle question.');
        console.error(err);
      });
  }
  
  /**
   * Trigger that fetch all possible questions and user has to select one to replace selected one.
   * @param selectedQuestionIndex 
   */
  async function onSelectQuestionToChange(myExamContent:ExamContentElement,myContentIndex:number) {
    setSelectedExamContent(myExamContent);
    setLoadingTip('Loading questions...');
    setLoading(true);
    const examApi = new NavigationExamApi(new ApiCaller(currentUser.token));
    examApi.loadExamContentsByParams(currentDoc.syllabusId as string, currentDoc.moduleId as string,myContentIndex+1).then((data) => {
      setExamQuestionContents(data.filter(c => !currentExam.examContent.content.find(ex => ex.questionId === c.questionId)));
      setIsQuestionSelectorOpen(true);
      setLoading(false);
    }).catch((err) => {
      setLoading(false);
      console.error(err);
      toast.error('Unable to open question selector due an error.');
    })
  }

  function onRefreshQuestion(examContent: ExamContentElement) {
    setSelectedQuestionForRefresh(examContent);
    setRefreshComment('');
    setRefreshConfirm(true);
  }

  function handleRefreshQuestion() {
    if (!selectedQuestionForRefresh || !refreshComment.trim()) {
      toast.error('Please provide a comment for the update.');
      return;
    }

    setLoading(true);
    setLoadingTip('Updating question...');
    setRefreshConfirm(false);

    const questionId = selectedQuestionForRefresh.questionId;
    const regex = getRegex(questionId);
    const currentContent = currentDoc.content;
    
    if (!currentContent) {
      toast.error('No content found in document.');
      setLoading(false);
      return;
    }

    const matchedContent = currentContent.match(regex);
    
    if (!matchedContent || !matchedContent[1]) {
      toast.error('Unable to extract question content from exam editor.');
      setLoading(false);
      return;
    }

    // Remove the h2 title (e.g., "Question 1 (20 Marks)") which is exclusive to the exam
    let examContent = matchedContent[1];
    examContent = examContent.replace(/<h2[^>]*>.*?<\/h2>/i, '').trim();
    
    const questionApi = new QuestionApi(new ApiCaller(currentUser.token));

    questionApi.updateQuestionFromExam(questionId, examContent, refreshComment)
      .then(() => {
        setLoading(false);
        toast.success('Question updated successfully in Question Bank.');
        setRefreshComment('');
        setSelectedQuestionForRefresh(undefined);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        toast.error('Unable to update question. Please try again.');
      });
  }

  /**
   * Handler for selected question to replace
   * @param selected
   */
  function handleSelectedQuestion(selected:ExamContentElement) {
    const questionIdToReplace = selectedExamContent?.questionId;
    const newQuestionId = selected.questionId;
    const examApi = new NavigationExamApi(new ApiCaller());
    examApi
      .replaceExamQuestion(
        currentDoc.id as string,
        currentExam.id as string,
        questionIdToReplace,
        newQuestionId,
        currentUser.token,
      )
      .then((result) => {
        let myDocument = new MYDocument(result.doc);
        let myExam = new Exam(result.exam);
        const examContent = myExam.examContent.content;
        const newQuestion = examContent.find(
                (ec: any) => ec.questionId === newQuestionId,
        );
        const regex = getRegex(questionIdToReplace);
        const existQuestion = currentDoc.content?.match(regex);

        if (existQuestion && newQuestion) {
          myDocument.content = currentDoc.content?.replace(
            regex,
            `<div id="${newQuestionId}">${selected.html}</div>`,
          );
            // Replace the old question with the new one in the exam content array
            myExam.examContent.content = myExam.examContent.content.map(item => 
            item.questionId === newQuestionId ? selected : item
            );

          if (currentExam.metadata.relatedDocuments.length) {
            const resources = currentExam.metadata.relatedDocuments.map(
              (relDoc: any) => ({ ...relDoc, id: relDoc._id }),
            );
            examApi
              .addExamResources(currentExam.id!, resources, currentUser.token)
              .then(() => {
                console.info('Resources was added');
              });
          }
          myExam.metadata = currentExam.metadata;

          dispatch(setCurrentExam(myExam));
          dispatch(setCurrentDoc(myDocument));

          editorContRef.current.setContent(myDocument.content);
          saveDraft(myDocument?.id, myDocument.content);
        } else {
          myDocument.content = currentDoc.content;
        }

      })
      .catch((err) => {
        console.error(err);
        toast.error('Unable to replace question.');
      }).finally(() => {
        toast.success('Question has been replaced.');
        setIsQuestionSelectorOpen(false);
      });
  }

  const onQuestionClick = useCallback(
    (event: any, question: ExamContentElement) => {
      if (!currentExam.id) {
        return;
      }
      {/* 2024-06-24: Button with class 'remove-question-button' is hidden
        because there is no way to restore a question if it was removed accidentally */}
      if (event.target.className.includes('remove-question-button')) {
        // Delete question
        const examApi = new NavigationExamApi(new ApiCaller());
        const questionId = question.questionId;
        setLoadingTip('Removing question...');
        setLoading(true);
        examApi
          .removeExamQuestion(
            currentDoc.id as string,
            currentExam.id,
            question.questionName,
            currentUser.token,
          )
          .then((res) => {
            setLoading(false);
            let myDocument = new MYDocument(res.doc);
            const regex = getRegex(questionId);

            const existQuestion = currentDoc.content?.match(regex);
            if (existQuestion) {
              myDocument.content = currentDoc.content?.replace(regex, '');
            }
            dispatch(setCurrentDoc(myDocument));
            let myExam: any = new Exam(res.exam);
            myExam = {
              ...myExam,
              metadata: currentExam.metadata,
            };
            if (currentExam.metadata?.relatedDocuments?.length) {
              const resources = currentExam.metadata.relatedDocuments.map(
                (relDoc: any) => ({ ...relDoc, id: relDoc._id }),
              );
              examApi
                .addExamResources(currentExam.id!, resources, currentUser.token)
                .then(() => {
                  console.info('Resources was added');
                });
            }
            dispatch(setCurrentExam(myExam));
            editorContRef.current.setContent(myDocument.content);
            toast.success('Question removed.');
            saveDraft(myDocument?.id, myDocument.content);
          })
          .catch((err:any) => {
            setLoading(false);
            console.error(err);
          });
      } else if (event.target.className.includes('shuffle-question-button')) {
        // Shuffle/Update question
        console.log('Shuffle question; ' + question.questionName);
        const examApi = new NavigationExamApi(new ApiCaller());
        setLoadingTip('Shuffling question...');
        setLoading(true);
        const questionId = question.questionId;
        examApi
          .shuffleExamQuestion(
            currentDoc.id as string,
            currentExam.id,
            questionId,
            question.topicId,
            currentUser.token,
          )
          .then((res) => {
            setLoading(false);
            let myDocument = new MYDocument(res.doc);
            const examContent = res.exam.examContent.content;
            const currentQuestionIDs = currentExam.examContent.content.map(
              (ce: any) => ce.questionId,
            );
            const newQuestionIDs = examContent.map(
              (content: any) => content.questionId,
            );
            const newQuestionId = newQuestionIDs.find(
              (id: string) => !currentQuestionIDs.includes(id),
            );
            if (newQuestionId) {
              const newQuestion = examContent.find(
                (ec: any) => ec.questionId === newQuestionId,
              );
              const regex = getRegex(questionId);

              const existQuestion = currentDoc.content?.match(regex);
              if (existQuestion && newQuestion) {
                myDocument.content = currentDoc.content?.replace(
                  regex,
                  `<div id="${newQuestionId}">${newQuestion.html}</div>`,
                );
              }
            } else {
              myDocument.content = currentDoc.content;
            }
            let myExam: any = new Exam(res.exam);

            if (currentExam.metadata.relatedDocuments.length) {
              const resources = currentExam.metadata.relatedDocuments.map(
                (relDoc: any) => ({ ...relDoc, id: relDoc._id }),
              );
              examApi
                .addExamResources(currentExam.id!, resources, currentUser.token)
                .then(() => {
                  console.info('Resources was added');
                });
            }
            myExam = {
              ...myExam,
              metadata: currentExam.metadata,
            };
            dispatch(setCurrentExam(myExam));
            dispatch(setCurrentDoc(myDocument));

            editorContRef.current.setContent(myDocument.content);
            toast.success('Question updated.');
            saveDraft(myDocument?.id, myDocument.content);
          })
          .catch((err) => {
            setLoading(false);
            toast.error('Unable to shuffle question.');
            console.error(err);
          });
      } else {
        // Get One Question
        const questionApi = new QuestionApi(new ApiCaller(currentUser.token));
        questionApi
          .getQuestionById(question.questionId)
          .then((question) => {
            Settings.setCurrentAdminQuestion(question);
            const layout =
              currentUser.type === 'admin'
                ? 'navozyme'
                : currentUser.type.toLowerCase();
            history.push(`/${layout}/question/detail`);
          })
          .catch((err) => {
            toast.error('Unable to open question details.');
            console.log(err);
          });
      }
    },
    [history, currentUser, currentExam, currentDoc],
  );

  const removeExamResourceFromDraft = useCallback(
    (resource) => {
      if (!currentExam.id) {
        return;
      }
      const examApi = new NavigationExamApi(new ApiCaller());
      setLoadingTip('Removing resource...');
      setLoading(true);
      examApi
        .removeExamResource(currentExam.id as string, resource._id, currentUser.token)
        .then((data) => {
          setLoading(false);
          dispatch(setCurrentExam(data));
          toast.success('Resource removed.');
        })
        .catch((err) => {
          setLoading(false);
          console.error(err);
        });
    },
    [currentExam, currentUser],
  );

  const closeResourceModal = () => {
    setResourcesToAdd([]);
    setResourceModal(false);
  };

  const onResourceChange = (e: any) => {
    setResourcesToAdd(e);
  };

  const addResources = useCallback(() => {
    if (!currentExam.id) {
      return;
    }
    const examApi = new NavigationExamApi(new ApiCaller());
    setLoadingTip('Adding resources...');
    setResourceModal(false);
    setLoading(true);
    examApi
      .addExamResources(currentExam.id as string, resourcesToAdd, currentUser.token)
      .then((data) => {
        setLoading(false);
        setResourcesToAdd([]);
        dispatch(setCurrentExam(data));
        toast.success('Resources added.');
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  }, [currentExam, currentUser, resourcesToAdd]);

  const onAddResourcesClick = () => {
    if (resources.length) {
      setResourceModal(true);
    } else {
      toast.info('No resources available.');
    }
  };

  const onPDFClick = async (document: MYDocument) => {
    try {
      setGeneratingPDF(true);
      const examDocApi = new ExamDocApi(new ApiCaller());
      const response = await examDocApi.sendToWritePDF(document.id, b64());
      if (response) {
        // Create a blob from the PDF data
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Open PDF in new window
        const pdfWindow = window.open(url, '_blank');
        if (!pdfWindow) {
          toast.error('Please allow pop-ups to view the PDF');
        }
        
        // Clean up the URL after a short delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        toast.error('There was an error creating the PDF file for the exam');
      }
    } catch (error) {
      console.error("Error onPDFClick", (error as any).message);
      toast.error('Error generating PDF preview');
    }
    setGeneratingPDF(false);
  };

  return (
    <>
      <div className="content">
        <LoadingSpinner spinning={loading} tip={loadingTip}>
          <DetailLayout title={'Go back'}>
            <Row>
              {currentExam && questionListIsEmpty() && (
                <Col className="text-center" lg="5" md="12">
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h4">Questions</CardTitle>
                    </CardHeader>
                    <CardBody className="table-full-width table-hover exam-editor-question-table">
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Topic</th>
                            {currentDoc.isSeto && 
                            <>
                              <th>Times used</th>
                              <th>Creation date</th>
                            </>
                            }
                            <th className="text-right">Marks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentExam.examContent.content.map(
                            (examContent: ExamContentElement, index:number) => {
                              const myQuestion = questionsWithUsage.get(examContent.questionId); // Get from map possible question
                              return (
                              <tr
                                key={examContent.questionName}
                                className="question-row">
                                <td onClick={(event) => onQuestionClick(event, examContent)}>{examContent.questionName}</td>
                                <td onClick={(event) => onQuestionClick(event, examContent)}>{examContent.topicName}</td>
                                {currentDoc.isSeto &&
                                <>
                                  <td onClick={(event) => onQuestionClick(event, examContent)}>{myQuestion?.timesUsed || 0}</td>
                                  <td onClick={(event) => onQuestionClick(event, examContent)}>{ myQuestion?.date ? ISOStringToDMY(myQuestion.date as any) : '-'}</td>
                                </>
                                }
                                <td onClick={(event) => onQuestionClick(event, examContent)} className="text-right">{examContent.marker}</td>
                                <td>
                                  {!currentDoc.isSeto && 
                                  <AntdButton
                                    className="btn-round btn-icon btn-icon-mini btn-neutral shuffle-question-button"
                                    color="danger"
                                    title="Shuffle this question"
                                    onClick={() => onShuffleQuestion(examContent)}
                                    >
                                    <i className="nc-icon nc-refresh-69" />
                                  </AntdButton>
                                  }
                                  { currentDoc.isSeto && 
                                    <AntdButton
                                    className="btn-round btn-icon btn-icon-mini btn-neutral shuffle-question-button"
                                    color="danger"
                                    title="Replace this question manually"
                                    onClick={() => onSelectQuestionToChange(examContent,index)}
                                    >
                                    <i className="nc-icon nc-settings" />
                                  </AntdButton>
                                  }
                                  <AntdButton
                                    className="btn-round btn-icon btn-icon-mini btn-neutral refresh-question-button"
                                    color="info"
                                    title="Update the question content in the Question Bank"
                                    onClick={() => onRefreshQuestion(examContent)}
                                    style={{ marginLeft: '5px' }}
                                    >
                                    <i className="nc-icon nc-cloud-upload-94" />
                                  </AntdButton>
                                </td>
                              </tr>
                            )},
                          )}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h4">
                        Resources&nbsp;&nbsp;
                        <AntdButton
                          onClick={() => onAddResourcesClick()}
                          className="btn-round btn-icon btn-icon-mini btn-neutral"
                          color="info"
                          title="Add Resources">
                          <i className="nc-icon nc-simple-add" />
                        </AntdButton>
                      </CardTitle>
                    </CardHeader>
                    <CardBody className="table-full-width table-hover exam-editor-resource-table">
                      <Table responsive>
                        <tbody>
                          {currentExam.metadata.relatedDocuments &&
                            currentExam.metadata.relatedDocuments.map(
                              (resource: ExamResource) => (
                                <tr key={resource.id}>
                                  <td>{resource.name}</td>
                                  <td>
                                    <AntdButton
                                      onClick={() => {
                                        if (resource.resource) {
                                          downloadDocument(
                                            resource.resource?.fileHash,
                                            resource.resource?.displayName,
                                            currentUser.token,
                                          );
                                        }
                                      }}
                                      disabled={!resource.resource}
                                      className="btn-round btn-icon btn-icon-mini btn-neutral"
                                      title="Download Resource">
                                      <i className="nc-icon nc-cloud-download-93" />
                                    </AntdButton>
                                  </td>
                                  <td>
                                    <AntdButton
                                      onClick={() =>
                                        removeExamResourceFromDraft(resource)
                                      }
                                      className="btn-round btn-icon btn-icon-mini btn-neutral"
                                      title="Remove From Exam">
                                      <i className="nc-icon nc-simple-remove" />
                                    </AntdButton>
                                  </td>
                                </tr>
                              ),
                            )}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>
              )}
              <Col lg={`${questionListIsEmpty() ? 7 : 12}`} md="12">
                <Card>
                  <CardHeader>
                    <h4 className="card-title">
                      {currentDoc.type === NZDocumentType.DRAFT_EXAM
                        ? `Design Exam ${customName ?? ''}`
                        : `Design Exam Pool Draft ${
                            currentDoc.serialNumber ?? ''
                          }`}
                    </h4>
                  </CardHeader>
                  <CardBody>
                    {currentDoc ? (
                      <div>
                        <div className="exam-editor-buttons">
                          {generatingPDF ? (
                            <LoadingSpinner spinning={true} />
                          ) : (
                            <Button
                              className="btn-round btn btn-danger"
                              onClick={() => onPDFClick(currentDoc)}>
                              <i className="nc-icon nc-simple-remove" /> PDF
                            </Button>
                          )}
                          {currentExam && (
                            <Button
                              className="btn-round btn btn-danger"
                              onClick={() =>
                                downloadAnswers(
                                  currentExam.id!,
                                  customName,
                                )
                              }>
                              <i className="nc-icon nc-simple-remove" />
                              Grading Scheme
                            </Button>
                          )}
                          <Button
                            className="btn-round btn btn-info"
                            onClick={() => saveDraft()}>
                            <i className="nc-icon nc-simple-remove" />
                            Save
                          </Button>
                          {currentExam && (
                            <Button
                              className="btn-round btn btn-info"
                              onClick={() => setShuffleConfirm(true)}>
                              <i className="nc-icon nc-refresh-69" />
                              Shuffle
                            </Button>
                          )}
                        </div>
                        {shuffleConfirm && (
                          <ReactBSAlert
                            warning
                            style={{ display: 'block', marginTop: '-100px' }}
                            title={'Exam Shuffle'}
                            onConfirm={shuffleDraft}
                            onCancel={() => setShuffleConfirm(false)}
                            confirmBtnBsStyle="info"
                            cancelBtnBsStyle="danger"
                            confirmBtnText={'Shuffle'}
                            cancelBtnText="Cancel"
                            showCancel
                            btnSize="">
                            Are you sure you want to shuffle all exam questions?
                          </ReactBSAlert>
                        )}
                        <Modal isOpen={refreshConfirm}>
                          <ModalHeader>
                            Update Question: {selectedQuestionForRefresh?.questionName}
                          </ModalHeader>
                          <ModalBody>
                            <p>This will update the question content in the Question Bank with the current content from the exam editor.</p>
                            <FormGroup>
                              <label>Comment (required):</label>
                              <Input
                                type="textarea"
                                placeholder="Please provide a comment describing the changes made"
                                value={refreshComment}
                                onChange={(e) => setRefreshComment(e.target.value)}
                                rows={4}
                              />
                            </FormGroup>
                          </ModalBody>
                          <div className="modal-footer" style={{ padding: '15px' }}>
                            <Button
                              color="secondary"
                              onClick={() => {
                                setRefreshConfirm(false);
                                setRefreshComment('');
                                setSelectedQuestionForRefresh(undefined);
                              }}
                              style={{ marginRight: '10px' }}>
                              Cancel
                            </Button>
                            <Button
                              color="info"
                              onClick={handleRefreshQuestion}>
                              Update Question
                            </Button>
                          </div>
                        </Modal>
                        <div className="">
                          {examDoc.content && (
                            <TinyMCETextEditor
                              onChange={updateDoc}
                              autoresize={false}
                              value={examDoc.content}
                              getEditor={getEditor}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <p>No current document loaded</p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col lg="12" md="12">
                {currentDoc &&
                  <ExamDocumentStatuses document={currentDoc} />
                }
              </Col>
            </Row>
          </DetailLayout>
          <Modal centered isOpen={resourceModal}>
            <ModalHeader>
              Add Resources
              <button
                aria-hidden={true}
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={closeResourceModal}>
                <i className="nc-icon nc-simple-remove" />
              </button>
            </ModalHeader>
            <ModalBody>
              <Form className="form-horizontal">
                <Row>
                  <Col sm="12">
                    <FormGroup>
                      <Select
                        isMulti
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="resources"
                        value={resourcesToAdd}
                        onChange={onResourceChange}
                        options={resources}
                        getOptionLabel={(item) => item.name || ''}
                        getOptionValue={(item) => item.id || ''}
                        placeholder="Choose question resources"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col className="text-center" sm="12">
                    <Button
                      className="btn-round btn btn-info"
                      onClick={addResources}>
                      Save
                    </Button>
                    <Button
                      className="btn-round btn btn-danger"
                      onClick={closeResourceModal}>
                      Close
                    </Button>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
          {/* Question selector modal */}
          <Modal
            centered
            size='xl'
            isOpen={isQuestionSelectorOpen}
            toggle={() => setIsQuestionSelectorOpen(!isQuestionSelectorOpen)}>
            <ModalHeader>
              <button
                aria-hidden={true}
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={() => setIsQuestionSelectorOpen(false)}>
                <i className="nc-icon nc-simple-remove" />
              </button>
              Question selector for document {currentDoc.serialNumber}
            </ModalHeader>
            <ModalBody style={{ padding: '0' }}>
              <QuestionSelector listOfContents={examQuestionContents} currentContent={selectedExamContent as ExamContentElement}  onSelectQuestion={handleSelectedQuestion}></QuestionSelector>
            </ModalBody>
          </Modal>
        </LoadingSpinner>
      </div>
      <ToastContainer />
    </>
  );
};

export default ExamEditor;
